import { useState, useEffect, useCallback, Component, lazy, Suspense } from 'react'
import Login from './pages/Login.jsx'
import Register from './pages/Register.jsx'
import Dashboard from './pages/Dashboard.jsx'
// Seiten werden lazy geladen (Code-Splitting): jede Seite ist ein eigenes
// Chunk, das erst beim ersten Aufruf nachgeladen wird — kleinere Startladung.
const Emails = lazy(() => import('./pages/Emails.jsx'))
const Contacts = lazy(() => import('./pages/Contacts.jsx'))
const Projects = lazy(() => import('./pages/Projects.jsx'))
const Projekt = lazy(() => import('./pages/Projekt.jsx'))
const Agent = lazy(() => import('./pages/Agent.jsx'))
const Chat = lazy(() => import('./pages/Chat.jsx'))
const Calendar = lazy(() => import('./pages/Calendar.jsx'))
const Gantt = lazy(() => import('./pages/Gantt.jsx'))
const Brain = lazy(() => import('./pages/Brain.jsx'))
const Ablage = lazy(() => import('./pages/Ablage.jsx'))
const Briefe = lazy(() => import('./pages/Briefe.jsx'))
const Notizen = lazy(() => import('./pages/Notizen.jsx'))
const Timesheet = lazy(() => import('./pages/Timesheet.jsx'))
const Fahrtenbuch = lazy(() => import('./pages/Fahrtenbuch.jsx'))
const KV = lazy(() => import('./pages/KV.jsx'))
const KVExcel = lazy(() => import('./pages/KVExcel.jsx'))
const KVExcelEditor = lazy(() => import('./pages/KVExcelEditor.jsx'))
const GrobKS = lazy(() => import('./pages/GrobKS.jsx'))
const Mutationen = lazy(() => import('./pages/Mutationen.jsx'))
const CostOverview = lazy(() => import('./pages/CostOverview.jsx'))
const Rechnungspruefung = lazy(() => import('./pages/Rechnungspruefung.jsx'))
const Zahlungsjournal = lazy(() => import('./pages/Zahlungsjournal.jsx'))
const Ausschreibungen = lazy(() => import('./pages/Ausschreibungen.jsx'))
const Bauabrechnung = lazy(() => import('./pages/Bauabrechnung.jsx'))
const Bautagebuch = lazy(() => import('./pages/Bautagebuch.jsx'))
const Regierapporte = lazy(() => import('./pages/Regierapporte.jsx'))
const Abnahme = lazy(() => import('./pages/Abnahme.jsx'))
const Submission = lazy(() => import('./pages/Submission.jsx'))
const Vergabe = lazy(() => import('./pages/Vergabe.jsx'))
const Planversand = lazy(() => import('./pages/Planversand.jsx'))
const Garantie = lazy(() => import('./pages/Garantie.jsx'))
const Behoerden = lazy(() => import('./pages/Behoerden.jsx'))
const Uebergabe = lazy(() => import('./pages/Uebergabe.jsx'))
const Werkvertraege = lazy(() => import('./pages/Werkvertraege.jsx'))
const Versicherungen = lazy(() => import('./pages/Versicherungen.jsx'))
const Material = lazy(() => import('./pages/Material.jsx'))
const Aufmass = lazy(() => import('./pages/Aufmass.jsx'))
const Honorar = lazy(() => import('./pages/Honorar.jsx'))
const Fotos = lazy(() => import('./pages/Fotos.jsx'))
const Preisdatenbank = lazy(() => import('./pages/Preisdatenbank.jsx'))
const Vorgaenge = lazy(() => import('./pages/Vorgaenge.jsx'))
const Settings = lazy(() => import('./pages/Settings.jsx'))
const Activity = lazy(() => import('./pages/Activity.jsx'))
import Sidebar from './components/Sidebar.jsx'
import AgentWidget from './components/AgentWidget.jsx'
import SearchSpotlight from './components/SearchSpotlight.jsx'
import OnboardingWizard from './components/OnboardingWizard.jsx'
import { ToastProvider, useToast } from './components/Toast.jsx'
import ReminderPostits from './components/ReminderPostits.jsx'
import ModuleBacklinkBar from './components/ModuleBacklinkBar.jsx'
import { api } from './lib/api.js'
import { applyTheme, subscribeSystemTheme } from './lib/theme.js'
import { setScheme } from './lib/scheme.js'

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(err, info) { console.error('[ErrorBoundary]', err, info) }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ marginBottom: 8 }}>Etwas ist schiefgelaufen</h2>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: 16 }}>{this.state.error.message}</p>
          <button onClick={() => this.setState({ error: null })} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid var(--color-border-light)', cursor: 'pointer' }}>
            Erneut versuchen
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <ToastProvider>
      <AppInner />
    </ToastProvider>
  )
}

const PAGES = ['emails', 'dashboard', 'projects', 'chat', 'calendar', 'ablage', 'briefe', 'timesheet', 'fahrtenbuch', 'kv', 'kv-excel', 'kv-excel-edit', 'grobks', 'kostenuebersicht', 'preisdatenbank', 'mutationen', 'rechnungspruefung', 'zahlungsjournal', 'ausschreibungen', 'bauabrechnung', 'bautagebuch', 'regierapporte', 'abnahme', 'submission', 'vergabe', 'planversand', 'garantie', 'behoerden', 'uebergabe', 'werkvertraege', 'versicherungen', 'material', 'aufmass', 'honorar', 'fotos', 'vorgaenge', 'activity', 'settings', 'gantt', 'agent', 'contacts', 'brain']

function pageFromHash() {
  const h = window.location.hash.replace('#/', '').replace('#', '').split('?')[0]
  if (h.startsWith('projekt/')) return 'projekt'
  return PAGES.includes(h) ? h : 'dashboard'
}

// Projekt-ID aus '#/projekt/<id>' — eigene Seite pro Projekt (projektzentrierte Navigation)
function projektIdFromHash() {
  const h = window.location.hash.replace('#/', '').replace('#', '').split('?')[0]
  if (!h.startsWith('projekt/')) return null
  return parseInt(h.slice('projekt/'.length), 10) || null
}

const MOBILE_MENU_GROUPS = [
  { label: 'Übersicht', ids: ['dashboard', 'projects', 'gantt'] },
  { label: 'Kommunikation', ids: ['emails', 'contacts', 'chat'] },
  { label: 'Planung', ids: ['calendar', 'timesheet', 'fahrtenbuch'] },
  { label: 'Kosten', ids: ['kv', 'grobks', 'preisdatenbank', 'mutationen', 'submission', 'rechnungspruefung', 'zahlungsjournal', 'bauabrechnung', 'honorar', 'vergabe', 'werkvertraege'] },
  { label: 'Dokumente', ids: ['ablage', 'briefe', 'fotos', 'planversand'] },
  { label: 'Baustelle', ids: ['bautagebuch', 'regierapporte', 'abnahme', 'uebergabe', 'aufmass', 'garantie', 'material', 'behoerden', 'versicherungen', 'vorgaenge'] },
  { label: 'Wissen', ids: ['brain'] },
]

const MOBILE_PAGE_LABELS = {
  emails: 'Posteingang', dashboard: 'Pendenzen', projects: 'Projekte', projekt: 'Projekt', chat: 'Urs',
  calendar: 'Kalender', ablage: 'Ablage', briefe: 'Briefe', notizen: 'Notizen', timesheet: 'Stundenliste', fahrtenbuch: 'Fahrtenbuch', kv: 'Baukosten', grobks: 'Kostenschätzung',
  preisdatenbank: 'Preisdatenbank', mutationen: 'Mutationen', kostenuebersicht: 'Kostenübersicht', rechnungen: 'Rechnungen', rechnungspruefung: 'Rechnungen', zahlungsjournal: 'Zahlungsjournal',
  bauabrechnung: 'Bauabrechnung', bautagebuch: 'Bautagebuch', regierapporte: 'Regierapporte',
  abnahme: 'Abnahme', submission: 'Submission', vergabe: 'Vergabe', planversand: 'Planversand',
  garantie: 'Garantien', behoerden: 'Behörden', uebergabe: 'Übergabe',
  werkvertraege: 'Werkverträge', versicherungen: 'Versicherungen', material: 'Material',
  aufmass: 'Aufmass', honorar: 'Honorar', fotos: 'Fotos', agent: 'Aktionen',
  contacts: 'Kontakte', settings: 'Einstellungen', gantt: 'Bauablaufplan', brain: 'Wissen', vorgaenge: 'Vorgänge',
}

function AppInner() {
  const showToast = useToast()
  const [page, setPageRaw] = useState(pageFromHash)
  const [projektId, setProjektId] = useState(projektIdFromHash)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileModules, setMobileModules] = useState(null)
  const [setupStatus, setSetupStatus] = useState(null)
  const [showWizard, setShowWizard] = useState(false)
  const [authMode, setAuthMode] = useState('login')

  const setPage = useCallback((p) => {
    setPageRaw(p)
    window.location.hash = '#/' + p
  }, [])

  // Sync hash → page on browser back/forward
  useEffect(() => {
    function onHashChange() { setPageRaw(pageFromHash()); setProjektId(projektIdFromHash()) }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  // Theme: System-Mode hört live auf prefers-color-scheme. Mount-applyTheme passiert schon in main.jsx.
  useEffect(() => subscribeSystemTheme(() => applyTheme()), [])

  // Keyboard shortcuts: Cmd+1-9 for page navigation
  useEffect(() => {
    function onKeyDown(e) {
      if (!(e.metaKey || e.ctrlKey)) return
      const num = parseInt(e.key)
      if (num >= 1 && num <= PAGES.length) {
        e.preventDefault()
        setPage(PAGES[num - 1])
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [setPage])

  useEffect(() => {
    // Kryptische cu_*-Query-Params aus Alt-/Einladungs-Links putzen — Urs nutzt
    // sie nicht, beim Hash-Routing blieben sie ewig kleben (enthielten teils
    // Passwoerter im Klartext). Hash + echte Params bleiben erhalten.
    const sp = new URLSearchParams(window.location.search)
    let cleaned = false
    for (const key of [...sp.keys()]) {
      if (key.startsWith('cu_')) { sp.delete(key); cleaned = true }
    }
    if (cleaned) {
      const qs = sp.toString()
      window.history.replaceState({}, '', (qs ? '?' + qs : window.location.pathname) + window.location.hash)
    }

    // Support token via URL param (for auto-login)
    const urlToken = new URLSearchParams(window.location.search).get('token')
    if (urlToken) {
      localStorage.setItem('agent_token', urlToken)
      window.history.replaceState({}, '', '/#/chat')
    }
    const token = localStorage.getItem('agent_token')
    if (!token) {
      setLoading(false)
      return
    }
    api.me()
      .then(u => { setUser(u); loadStats(); loadSetupStatus(); loadBrandingFont(); loadMobileModules(); loadColorScheme() })
      .catch(() => { localStorage.removeItem('agent_token'); })
      .finally(() => setLoading(false))
  }, [])

  // Anwesenheit melden, solange das Fenster wirklich sichtbar ist. Nur daraufhin
  // fragt der Server das Postfach ab — ein Werkzeug fuer eine Person muss nachts und
  // am Wochenende nicht alle fuenf Minuten am Mailserver anklopfen. Ein Tab im
  // Hintergrund zaehlt bewusst nicht als anwesend.
  useEffect(() => {
    if (!user) return
    let gestoppt = false
    const melden = () => {
      if (gestoppt || document.visibilityState !== 'visible') return
      api.presence().catch(() => { /* offline oder abgemeldet — beim naechsten Mal wieder */ })
    }
    melden()
    const takt = setInterval(melden, 2 * 60 * 1000)
    document.addEventListener('visibilitychange', melden)
    return () => {
      gestoppt = true
      clearInterval(takt)
      document.removeEventListener('visibilitychange', melden)
    }
  }, [user])

  // Farbschema serverseitig pro User (greift auch geräteübergreifend / iOS-App).
  // localStorage hat es beim Boot schon flicker-frei angewandt; hier ggf. korrigieren.
  function loadColorScheme() {
    api.getUserSettings().then(s => {
      if (s?.color_scheme) setScheme(s.color_scheme)
    }).catch(() => { /* localStorage-Wert bleibt */ })
  }

  function loadBrandingFont() {
    api.brandingGet().then(b => {
      if (b.font_path) {
        const el = document.createElement('style')
        el.id = 'custom-font-style'
        el.textContent = `@font-face { font-family: 'CustomBrand'; src: url('${api.brandingFontUrl()}'); font-display: swap; }`
        document.head.appendChild(el)
        document.documentElement.style.setProperty('--font-brand', "'CustomBrand', var(--font-body)")
      }
    }).catch(() => {})
  }

  function loadMobileModules() {
    api.sidebarModules().then(mods => setMobileModules(mods)).catch(() => {})
  }

  function loadStats() {
    api.dashboard().then(d => setStats(d.stats)).catch(err => showToast(err.message || 'Dashboard konnte nicht geladen werden'))
  }

  function loadSetupStatus() {
    api.setupStatus().then(status => {
      setSetupStatus(status)
      if (!status.setupComplete) setShowWizard(true)
    }).catch(() => {})
  }

  function handleLogin(token) {
    localStorage.setItem('agent_token', token)
    api.me().then(u => { setUser(u); loadStats(); loadSetupStatus(); loadColorScheme() })
  }

  function handleLogout() {
    localStorage.removeItem('agent_token')
    setUser(null)
    setStats(null)
  }

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div className="spinner" /></div>
  }

  if (!user) {
    return authMode === 'register'
      ? <Register onLogin={handleLogin} onSwitchToLogin={() => setAuthMode('login')} />
      : <Login onLogin={handleLogin} onSwitchToRegister={() => setAuthMode('register')} />
  }

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <Dashboard onNavigate={setPage} onRefreshStats={loadStats} setupStatus={setupStatus} onOpenWizard={(s) => { setShowWizard(true) }} />
      case 'emails': return <Emails />
      case 'contacts': return <Contacts />
      case 'projects': return <Projects />
      case 'projekt': return <Projekt key={projektId} projectId={projektId} />
      case 'chat': return <Chat onRefreshStats={loadStats} user={user} />
      case 'agent': return <Agent onRefreshStats={loadStats} />
      case 'calendar': return <Calendar />
      case 'gantt': return <Gantt />
      case 'brain': return <Brain />
      case 'ablage': return <Ablage />
      case 'briefe': return <Briefe />
      case 'notizen': return <Notizen />
      case 'timesheet': return <Timesheet />
      case 'fahrtenbuch': return <Fahrtenbuch />
      case 'kv': return <KV />
      case 'kv-excel': return <KVExcel />
      case 'kv-excel-edit': return <KVExcelEditor />
      case 'grobks': return <GrobKS />
      case 'preisdatenbank': return <Preisdatenbank />
      case 'mutationen': return <Mutationen />
      case 'kostenuebersicht': return <CostOverview />
      case 'rechnungspruefung': return <Rechnungspruefung />
      case 'zahlungsjournal': return <Zahlungsjournal />
      case 'ausschreibungen': return <Ausschreibungen />
      case 'bauabrechnung': return <Bauabrechnung />
      case 'bautagebuch': return <Bautagebuch />
      case 'regierapporte': return <Regierapporte />
      case 'abnahme': return <Abnahme />
      case 'submission': return <Submission />
      case 'vergabe': return <Vergabe />
      case 'planversand': return <Planversand />
      case 'garantie': return <Garantie />
      case 'behoerden': return <Behoerden />
      case 'uebergabe': return <Uebergabe />
      case 'werkvertraege': return <Werkvertraege />
      case 'versicherungen': return <Versicherungen />
      case 'material': return <Material />
      case 'aufmass': return <Aufmass />
      case 'honorar': return <Honorar />
      case 'fotos': return <Fotos />
      case 'vorgaenge': return <Vorgaenge />
      case 'settings': return <Settings />
case 'activity': return <Activity />
      default: return <Dashboard onNavigate={setPage} />
    }
  }

  function handleMobileNavigate(p) {
    setPage(p)
    setMobileMenuOpen(false)
  }

  return (
    <div className={`app-layout ${page === 'chat' ? 'app-dark' : ''}`}>
      {/* Mobile Header */}
      <header className="mobile-header">
        <span className="mobile-header-title">{MOBILE_PAGE_LABELS[page] || 'Pendenzen'}</span>
        <button className="mobile-dots-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Mehr">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
          </svg>
        </button>
      </header>

      {/* Mobile "..." Menu Overlay */}
      {mobileMenuOpen && (() => {
        const visibleIds = new Set(
          mobileModules
            ? mobileModules.filter(m => m.visible).map(m => m.module_id)
            : ['emails', 'dashboard', 'projects', 'chat', 'calendar', 'ablage', 'timesheet', 'kv', 'mutationen']
        )
        return (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
            <nav className="mobile-menu" onClick={e => e.stopPropagation()}>
              {MOBILE_MENU_GROUPS.map(group => {
                const groupIds = group.ids.filter(id => visibleIds.has(id))
                if (groupIds.length === 0) return null
                return (
                  <div key={group.label}>
                    <div className="mobile-menu-group-header">{group.label}</div>
                    {groupIds.map(id => (
                      <button
                        key={id}
                        className={`mobile-menu-link ${page === id ? 'active' : ''}`}
                        onClick={() => handleMobileNavigate(id)}
                      >
                        {MOBILE_PAGE_LABELS[id] || id}
                      </button>
                    ))}
                  </div>
                )
              })}
              <button
                className={`mobile-menu-link ${page === 'settings' ? 'active' : ''}`}
                onClick={() => handleMobileNavigate('settings')}
              >
                Einstellungen
              </button>
              <button className="mobile-menu-link mobile-menu-logout" onClick={handleLogout}>Abmelden</button>
            </nav>
          </div>
        )
      })()}

      {/* Mobile Bottom Tab Bar */}
      <nav className="mobile-tab-bar">
        <button className={`mobile-tab ${page === 'dashboard' ? 'active' : ''}`} onClick={() => setPage('dashboard')}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          <span>Pendenzen</span>
        </button>
        <button className={`mobile-tab ${page === 'chat' ? 'active' : ''}`} onClick={() => setPage('chat')}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <circle cx="12" cy="5" r="2" />
            <path d="M12 7v4" />
            <circle cx="8.5" cy="16" r="1.5" fill="currentColor" stroke="none" />
            <circle cx="15.5" cy="16" r="1.5" fill="currentColor" stroke="none" />
          </svg>
          <span>Urs</span>
        </button>
        <button className={`mobile-tab ${page === 'agent' ? 'active' : ''}`} onClick={() => setPage('agent')}>
          <span className="mobile-tab-icon-wrap">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <path d="M9 14l2 2 4-4" />
            </svg>
            {(stats?.pendingActions || 0) > 0 && (
              <span className="mobile-tab-badge">{stats.pendingActions > 9 ? '9+' : stats.pendingActions}</span>
            )}
          </span>
          <span>Aktionen</span>
        </button>
      </nav>

      <Sidebar
        activePage={page}
        activeProjectId={projektId}
        onNavigate={setPage}
        user={user}
        stats={stats}
        onLogout={handleLogout}
      />
      <main className={`main-content ${page === 'chat' ? 'main-content-dark' : ''}`}>
        <ErrorBoundary>
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>}>
            <ModuleBacklinkBar page={page} />
            {renderPage()}
          </Suspense>
        </ErrorBoundary>
      </main>
      <AgentWidget pendingActions={stats?.pendingActions || 0} currentPage={page} onNavigate={setPage} onRefreshStats={loadStats} />
      <ReminderPostits />
      <SearchSpotlight onNavigate={setPage} />
      {showWizard && (
        <OnboardingWizard
          initialStep={setupStatus?.steps ? (!setupStatus.steps.companyName ? 0 : !setupStatus.steps.llmProvider ? 1 : !setupStatus.steps.email ? 2 : 3) : 0}
          onComplete={() => { setShowWizard(false); loadSetupStatus(); setPage('dashboard') }}
        />
      )}
    </div>
  )
}
